import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { siteContent, images } from "../../data/content";

const navLinks = [
  { href: "/", label: "Home" },
  { href: "/products", label: "Products" },
  { href: "/packaging", label: "Packaging" },
  { href: "/quality", label: "Quality" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  return (
    <>
      {/* Top Bar */}
      <div className="bg-charcoal-paper border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-2 flex justify-between items-center text-sm">
          <div className="flex items-center gap-4 md:gap-6">
            <a
              href={`mailto:${siteContent.contact.email}`}
              className="flex items-center gap-2 text-zinc-400 hover:text-coconut transition-colors"
              data-testid="topbar-email"
            >
              <Mail className="w-4 h-4" />
              <span className="hidden sm:inline">{siteContent.contact.email}</span>
            </a>
            <a
              href={siteContent.contact.whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 text-zinc-400 hover:text-[#25D366] transition-colors"
              data-testid="topbar-whatsapp"
            >
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">{siteContent.contact.whatsapp}</span>
            </a>
          </div>
          <div className="flex items-center gap-4">
            <span
              className="text-zinc-400 font-mono uppercase tracking-wider text-xs"
              data-testid="topbar-established"
            >
              Established 2016
            </span>
            <a
              href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#25D366] hover:underline font-medium"
              data-testid="topbar-wa-cta"
            >
              WhatsApp Us
            </a>
          </div>
        </div>
      </div>

      {/* Main Navbar */}
      <nav className="sticky top-0 z-50 bg-charcoal/95 backdrop-blur-lg border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3" data-testid="navbar-logo">
              <img 
                src={images.logo} 
                alt="Samudra Charcoal Logo" 
                className="h-12 md:h-14 w-auto object-contain"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-8">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={`text-sm font-medium transition-colors hover:text-coconut ${
                    location.pathname === link.href ? "text-coconut" : "text-zinc-300"
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Desktop CTA */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                asChild
                className="bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase tracking-wider"
                data-testid="nav-quote-btn"
              >
                <Link to="/contact">Request Quote</Link>
              </Button>
            </div>

            {/* Mobile Menu */}
            <Sheet open={isOpen} onOpenChange={setIsOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-charcoal border-white/10 w-[300px]">
                <div className="flex flex-col h-full pt-8">
                  <div className="flex flex-col gap-4">
                    {navLinks.map((link) => (
                      <Link
                        key={link.href}
                        to={link.href}
                        onClick={() => setIsOpen(false)}
                        className={`text-lg font-heading uppercase tracking-wider py-2 border-b border-white/5 transition-colors hover:text-coconut ${
                          location.pathname === link.href ? "text-coconut" : "text-white"
                        }`}
                        data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                      >
                        {link.label}
                      </Link>
                    ))}
                  </div>
                  <div className="mt-auto pb-8 space-y-4">
                    <Button
                      asChild
                      className="w-full bg-coconut text-charcoal hover:bg-coconut-hover font-heading uppercase"
                      data-testid="mobile-quote-btn"
                    >
                      <Link to="/contact" onClick={() => setIsOpen(false)}>
                        Request Quote
                      </Link>
                    </Button>
                    <Button
                      asChild
                      variant="outline"
                      className="w-full border-[#25D366] text-[#25D366] hover:bg-[#25D366]/10 font-heading uppercase"
                      data-testid="mobile-wa-btn"
                    >
                      <a
                        href={`${siteContent.contact.whatsappLink}?text=${encodeURIComponent(siteContent.contact.whatsappMessage)}`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        WhatsApp Us
                      </a>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>
    </>
  );
};
